import { useIsOnlineCore } from '@/hooks/utils';
import { useUserIp } from '@/hooks/utils/useUserIp';
import { type PropsWithChildren, createContext, useMemo } from 'react';

type OnlineStatusContextType = {
  isOnline: boolean;
  onlineStatus: string;
  userIp: string | null;
  checkOnlineStatus: () => void;
};

// Real default so surfaces without the provider (e.g. embeds) read as online
// instead of undefined — consumers can use `!isOnline` without guarding.
export const OnlineStatusContext = createContext<OnlineStatusContextType>({
  isOnline: true,
  onlineStatus: 'online',
  userIp: null,
  checkOnlineStatus: () => undefined,
});

// Module-readable mirror of the provider's `isOnline` (APL-12236 CN7-34).
// The data providers are plain modules, not React, so they cannot read the
// context — and `navigator.onLine` is NOT a substitute: it is the raw browser
// flag everywhere except iOS (useOverrideNavigator patches it for AAC mode
// only, APL-5761), so it stays `true` through a ping timeout / network_issue,
// which is exactly the degraded-link case this guards. Mirroring the ONE
// provider heartbeat keeps a single source of truth rather than adding a
// second, weaker notion of "online".
// Defaults to true so surfaces without the provider read as online, matching
// the context default above.
let onlineSnapshot = true;

/** Non-React read of the provider's live `isOnline`. */
export const getIsOnlineSnapshot = (): boolean => onlineSnapshot;

export const OnlineStatusContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  // const [isOnline, setIsOnline] = useState<boolean>(true);
  const { isOnline, onlineStatus, checkOnlineStatus } = useIsOnlineCore();
  const { userIp } = useUserIp();

  // Publish to the non-React mirror. Assigned during render (not in an effect)
  // so a module reading it mid-commit never sees a stale value.
  onlineSnapshot = isOnline;

  console.log(userIp, 'userIp in OnlineStatusContextProvider');
  // const [mode, setMode] = useState<string>('light');
  // useEffect(() => {
  //   const configFromLocalStorage = localStorage.getItem('config');
  //   if (configFromLocalStorage) {
  //     setConfig(JSON.parse(configFromLocalStorage));
  //   }
  // }, []);

  // useEffect(() => {
  //   window.localStorage.setItem('config', JSON.stringify(config));
  // }, [config]);

  // TODO: get default theme from os preferences - window.matchMedia('(prefers-color-scheme: dark)')
  // const setConfig = (config: ConfigType) => {
  //   setConfig(config);
  // };
  const finalValue = useMemo(() => {
    return {
      isOnline,
      onlineStatus,
      checkOnlineStatus,
      userIp,
    };
  }, [isOnline, onlineStatus, checkOnlineStatus, userIp]);

  return <OnlineStatusContext.Provider value={finalValue}>{children}</OnlineStatusContext.Provider>;
};
